import {
  createContext,
  ReactElement,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  defaultOidcFactory,
  getReturnTo,
  OidcClientLike,
  OidcConfig,
  OidcFactory,
  OidcSessionLike,
} from "./oidc-adapter.js";
import { AuthState, AuthStatus, AuthUser } from "./types.js";

const AuthContext = createContext<AuthState | null>(null);

export interface OidcAuthProviderProps {
  config: OidcConfig;
  /** Base URL of the Mesterplan API hosting the server-side auth endpoints. */
  apiBaseUrl?: string;
  /**
   * Login behavior at mount:
   *  - `check-sso` (default) checks the server-side session cookie and falls
   *    back to the unauthenticated state if no session exists.
   *  - `login-required` redirects immediately through the backend login
   *    endpoint when no session can be restored.
   */
  onLoad?: "check-sso" | "login-required";
  /** Test seam — inject a fake backend-auth client factory. */
  oidcFactory?: OidcFactory;
  children: ReactNode;
}

/**
 * Wrap the React tree once at the root. Children read auth state via
 * `useAuth()`. The browser flow stays cookie-based, so the provider's job is
 * session discovery plus login/logout redirects through the backend endpoints.
 */
export function OidcAuthProvider(props: OidcAuthProviderProps): ReactElement {
  const {
    config,
    apiBaseUrl = "",
    onLoad = "check-sso",
    oidcFactory = defaultOidcFactory,
    children,
  } = props;

  const clientRef = useRef<OidcClientLike | null>(null);
  if (clientRef.current === null) {
    clientRef.current = oidcFactory({ config, apiBaseUrl });
  }
  const client = clientRef.current;

  const [status, setStatus] = useState<AuthStatus>("initializing");
  const [user, setUser] = useState<AuthUser | null>(null);

  useEffect(() => {
    let cancelled = false;

    const applySession = (session: OidcSessionLike | null) => {
      if (cancelled) return;
      const nextUser = toAuthUser(session);
      setUser(nextUser);
      setStatus(nextUser ? "authenticated" : "unauthenticated");
    };

    void (async () => {
      try {
        const session = await client.getSession();
        if (cancelled) return;
        if (session) {
          applySession(session);
          return;
        }

        if (onLoad === "login-required") {
          await client.login(getReturnTo());
          return;
        }

        applySession(null);
      } catch (err) {
        if (cancelled) return;
        console.error("[auth-react] init failed", err);
        setUser(null);
        setStatus("unauthenticated");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [client, onLoad]);

  const value: AuthState = useMemo(
    () => ({
      status,
      user,
      async getToken() {
        return null;
      },
      async login() {
        await client.login(getReturnTo());
      },
      async logout() {
        await client.logout(new URL("/", window.location.origin).toString());
        setUser(null);
        setStatus("unauthenticated");
      },
    }),
    [status, user, client],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (value === null) {
    throw new Error("useAuth must be called inside <OidcAuthProvider>");
  }
  return value;
}

/**
 * Non-throwing variant: returns the auth state when mounted inside an
 * <OidcAuthProvider>, or `null` otherwise. Use this at call sites that must keep
 * working even when the OIDC provider is not configured (e.g. the local
 * Firebase-only path, where `NetAuthProvider` skips mounting the provider).
 */
export function useOptionalAuth(): AuthState | null {
  return useContext(AuthContext);
}

export function toAuthUser(session: OidcSessionLike | null): AuthUser | null {
  if (!session || typeof session.sub !== "string") return null;
  return {
    id: session.sub,
    email: typeof session.email === "string" ? session.email : "",
    displayName:
      (typeof session.name === "string" ? session.name : undefined) ??
      (typeof session.preferredUsername === "string" ? session.preferredUsername : undefined) ??
      "",
    roles: readRoles(session),
    orgId:
      (typeof session.orgId === "string" ? session.orgId : undefined) ??
      (typeof session.org_id === "string" ? session.org_id : undefined) ??
      (typeof session.activeOrg === "string" ? session.activeOrg : undefined) ??
      null,
  };
}

function readRoles(session: OidcSessionLike): ReadonlyArray<string> {
  const raw = session.realmRoles ?? session.role;
  if (Array.isArray(raw)) {
    return raw.filter((role): role is string => typeof role === "string");
  }
  if (typeof raw === "string") {
    return [raw];
  }
  return [];
}
